import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, Card, Input, Button, Avatar, Empty, Space, message as antMessage, Descriptions, Tag, Spin } from 'antd';
import {
  RobotOutlined,
  SendOutlined,
  UserOutlined,
  FileTextOutlined,
  CalendarOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import aiAgentService from '../services/aiAgentService';
import apiService from '../services/apiService';

const { TextArea } = Input;

// Validation functions
const validateName = (name) => {
  // Name should contain at least one letter and can include spaces, hyphens, apostrophes
  // Should not be only numbers or special characters
  const nameRegex = /^[a-zA-Z]+(([' -][a-zA-Z ])?[a-zA-Z]*)*$/;
  const hasLetter = /[a-zA-Z]/.test(name);
  return nameRegex.test(name.trim()) && hasLetter && name.trim().length >= 2;
};

const validateEmail = (email) => {
  // More strict email validation - only allows letters, numbers, dots, hyphens, underscores in local part
  // and proper domain format
  const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
};

const validatePhone = (phone) => {
  // Accept two formats:
  // 1. E.164 format with +: +1234567890 (+ followed by 7-15 digits)
  // 2. Without +: 1234567890 (10-15 digits)
  const withPlusRegex = /^\+[1-9]\d{6,14}$/;
  const withoutPlusRegex = /^[0-9]{10,15}$/;
  return withPlusRegex.test(phone) || withoutPlusRegex.test(phone);
};

const validateStatus = (status) => {
  const validStatuses = ['new', 'contacted', 'qualified', 'proposal', 'negotiation', 'won', 'lost'];
  return validStatuses.includes(status.toLowerCase());
};

const validatePriority = (priority) => {
  const validPriorities = ['low', 'medium', 'high', 'urgent'];
  return validPriorities.includes(priority.toLowerCase());
};

const AIAgentPage = () => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'ai',
      content: 'Hello! I am your AI assistant. I can help you create leads, deals, schedule meetings, and manage tasks. What would you like to do today?',
      timestamp: new Date().toLocaleTimeString(),
    },
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [activeAction, setActiveAction] = useState(null);
  const [formData, setFormData] = useState({});
  const [currentStep, setCurrentStep] = useState(0);
  const [isFormComplete, setIsFormComplete] = useState(false);
  const [typingMessage, setTypingMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [availableLeads, setAvailableLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingTeamMembers, setLoadingTeamMembers] = useState(false);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  const actionCards = [
    {
      key: 'lead',
      title: 'Create Lead',
      icon: <UserOutlined style={{ fontSize: '20px', color: '#1890ff' }} />,
      color: '#e6f7ff',
    },
    {
      key: 'edit-lead',
      title: 'Edit Lead',
      icon: <UserOutlined style={{ fontSize: '20px', color: '#13c2c2' }} />,
      color: '#e6fffb',
    },
    {
      key: 'assign-lead',
      title: 'Assign Lead',
      icon: <UserOutlined style={{ fontSize: '20px', color: '#eb2f96' }} />,
      color: '#fff0f6',
    },
    {
      key: 'deal',
      title: 'Create Deal',
      icon: <DollarOutlined style={{ fontSize: '20px', color: '#52c41a' }} />,
      color: '#f6ffed',
    },
    {
      key: 'meeting',
      title: 'Schedule Meeting',
      icon: <CalendarOutlined style={{ fontSize: '20px', color: '#faad14' }} />,
      color: '#fffbe6',
    },
    {
      key: 'task',
      title: 'Create Task',
      icon: <FileTextOutlined style={{ fontSize: '20px', color: '#722ed1' }} />,
      color: '#f9f0ff',
    },
  ];

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, typingMessage]);

  // Debug: Log when availableLeads changes
  useEffect(() => {
    console.log('=== STATE UPDATE ===');
    console.log('Available leads:', availableLeads.length, 'leads');
    console.log('Active action:', activeAction);
    console.log('Selected lead:', selectedLead);
    console.log('Loading leads:', loadingLeads);
    console.log('==================');
  }, [availableLeads, activeAction, selectedLead, loadingLeads]);

  // Typewriter effect for AI messages
  const typewriterEffect = (text, callback) => {
    setIsTyping(true);
    setTypingMessage('');
    let index = 0;
    
    const timer = setInterval(() => {
      if (index < text.length) {
        setTypingMessage((prev) => prev + text.charAt(index));
        index++;
      } else {
        clearInterval(timer);
        setIsTyping(false);
        setTypingMessage('');
        if (callback) callback();
      }
    }, 30); // Adjust speed here (lower = faster)

    return () => clearInterval(timer);
  };

  // Check if form is complete
  useEffect(() => {
    if (!activeAction) return;

    const requiredFieldsMap = {
      lead: ['firstName', 'lastName', 'email', 'phone', 'status', 'priority'],
      'edit-lead': ['leadEmail', 'fieldToUpdate', 'newValue'],
      'assign-lead': ['leadEmail', 'assignToUserId'],
      deal: ['title', 'value', 'closeDate', 'contactFirstName', 'contactLastName', 'contactEmail', 'stage', 'probability', 'source'],
      meeting: ['title', 'dateTime'],
      task: ['title', 'dueDate'],
    };

    const required = requiredFieldsMap[activeAction] || [];
    const complete = required.every(field => formData[field] && formData[field].toString().trim() !== '');
    setIsFormComplete(complete);
  }, [formData, activeAction]);

  const getNextQuestion = (action, step) => {
    const questions = {
      lead: [
        'Great! Let\'s create a lead. What is the first name? (Required)',
        'What is the last name? (Required)',
        'What is the email address? (Required)',
        'What is the phone number? (Required - 10-15 digits, with or without +)',
        'What is the status? (Required - new/contacted/qualified/proposal/negotiation/won/lost)',
        'What is the priority? (Required - low/medium/high/urgent)',
        'What is the company name? (Optional - press Enter to skip)',
      ],
      'edit-lead': [
        'What is the email address of the lead you want to edit? (Required)',
        'What field would you like to update? (firstName/lastName/email/phone/status/priority/company)',
        'What is the new value?',
      ],
      'assign-lead': [
        'What is the email address of the lead you want to assign? (Required)',
        'Who would you like to assign this lead to? (I\'ll show you the available team members)',
      ],
      deal: [
        'Perfect! Let\'s create a deal. What is the deal title? (Required)',
        'What is the deal value in dollars? (Required)',
        'What is the expected close date? (Required - format: YYYY-MM-DD)',
        'What is the contact\'s first name? (Required)',
        'What is the contact\'s last name? (Required)',
        'What is the contact\'s email? (Required)',
        'What is the deal stage? (Required - qualification/needs_analysis/proposal/negotiation/closed_won/closed_lost)',
        'What is the win probability percentage? (Required - 0-100)',
        'What is the lead source? (Required - inbound/outbound/referral/partner/other)',
      ],
      meeting: [
        'Excellent! Lets schedule a meeting. What is the meeting title?',
        'When should the meeting be? (date and time)',
        'How long will it be? (in minutes, Optional - press Enter to skip)',
        'What type of meeting? (call/video/in-person, Optional - press Enter to skip)',
      ],
      task: [
        'Great! Lets create a task. What is the task title?',
        'When is it due? (date)',
        'What is the priority? (low/medium/high, Optional - press Enter to skip)',
        'Any description? (Optional - press Enter to skip)',
      ],
    };

    return questions[action]?.[step] || 'All set! Review the details on the right and click Create when ready.';
  };

  const fetchTeamMembers = async () => {
    setLoadingTeamMembers(true);
    try {
      console.log('👥 Fetching team members...');
      const response = await apiService.get('/users/team-members');
      console.log('📦 Team members response:', response.data);
      
      if (response.data.success) {
        // Backend returns { success: true, data: { teamMembers: [...] } }
        const members = response.data.data?.teamMembers || [];
        console.log('✨ Team members loaded:', members.length);
        console.log('👤 Members:', members);
        
        // Transform to match expected structure (id -> _id, add firstName/lastName)
        const transformedMembers = members.map(m => ({
          _id: m.id,
          firstName: m.name.split(' ')[0],
          lastName: m.name.split(' ').slice(1).join(' '),
          email: m.email,
          role: m.role,
          avatar: m.avatar
        }));
        
        setTeamMembers(transformedMembers);
        console.log('✅ Transformed members:', transformedMembers);
        return transformedMembers;
      }
    } catch (error) {
      console.error('💥 Error fetching team members:', error);
      antMessage.error('Failed to fetch team members');
      return [];
    } finally {
      setLoadingTeamMembers(false);
    }
  };

  const searchLeadByEmail = async (email) => {
    setLoadingLeads(true);
    try {
      console.log('🔍 Searching for lead with email:', email);
      const response = await apiService.get(`/leads?search=${encodeURIComponent(email)}`);
      console.log('📦 Search response:', response.data);
      console.log('📊 Response structure:', {
        hasData: !!response.data.data,
        hasLeads: !!response.data.data?.leads,
        dataKeys: response.data.data ? Object.keys(response.data.data) : []
      });
      
      // Backend returns { data: { leads: [...], pagination: {...} } }
      const leads = response.data.data?.leads || [];
      console.log('👥 Found leads:', leads.length);
      
      if (leads.length > 0) {
        const lead = leads[0]; // Take the first match
        console.log('✨ Lead found:', lead);
        setSelectedLead(lead);
        const initialFormData = { leadEmail: email, leadId: lead._id };
        setFormData(initialFormData);
        setCurrentStep(1); // Move to next step (fieldToUpdate)
        console.log('📝 Initial formData set:', initialFormData);
        
        const aiContent = `Perfect! I found the lead: ${lead.firstName} ${lead.lastName} (${lead.email}). ${getNextQuestion('edit-lead', 1)}`;
        typewriterEffect(aiContent, () => {
          const aiMsg = {
            id: messages.length + 1,
            type: 'ai',
            content: aiContent,
            timestamp: new Date().toLocaleTimeString(),
          };
          setMessages((prev) => [...prev, aiMsg]);
        });
        return true;
      } else {
        console.log('❌ No lead found with email:', email);
        const aiContent = `I couldn't find a lead with email "${email}". Please check the email and try again, or create a new lead first.`;
        typewriterEffect(aiContent, () => {
          const aiMsg = {
            id: messages.length + 1,
            type: 'ai',
            content: aiContent,
            timestamp: new Date().toLocaleTimeString(),
          };
          setMessages((prev) => [...prev, aiMsg]);
        });
        // Reset to ask for email again
        setCurrentStep(0);
        setFormData({});
        return false;
      }
    } catch (error) {
      console.error('💥 Error searching lead:', error);
      antMessage.error('Failed to search lead: ' + (error.message || 'Unknown error'));
      setCurrentStep(0);
      setFormData({});
      return false;
    } finally {
      setLoadingLeads(false);
    }
  };

  const handleLeadSelect = (lead) => {
    setSelectedLead(lead);
    setFormData({ leadId: lead._id });
    
    const aiContent = `Great! I've selected ${lead.firstName} ${lead.lastName} (${lead.email}). ${getNextQuestion('edit-lead', 0)}`;
    typewriterEffect(aiContent, () => {
      const aiMsg = {
        id: messages.length + 1,
        type: 'ai',
        content: aiContent,
        timestamp: new Date().toLocaleTimeString(),
      };
      setMessages((prev) => [...prev, aiMsg]);
    });
  };

  const handleActionSelect = async (action) => {
    setFormData({});
    setCurrentStep(0);
    setIsFormComplete(false);
    setSelectedLead(null);
    setAvailableLeads([]); // Clear previous leads
    setActiveAction(action);

    const userMsg = {
      id: messages.length + 1,
      type: 'user',
      content: `I want to ${action === 'edit-lead' ? 'edit' : 'create'} a ${action.replace('edit-', '')}`,
      timestamp: new Date().toLocaleTimeString(),
    };

    setMessages([...messages, userMsg]);

    // Use typewriter effect for AI response
    const aiContent = getNextQuestion(action, 0);
    typewriterEffect(aiContent, () => {
      const aiMsg = {
        id: messages.length + 2,
        type: 'ai',
        content: aiContent,
        timestamp: new Date().toLocaleTimeString(),
      };
      setMessages((prev) => [...prev, aiMsg]);
    });
  };

  const detectActionFromMessage = (message) => {
    const lowerMsg = message.toLowerCase().trim();
    
    // Check for assign lead
    if ((lowerMsg.includes('assign') || lowerMsg.includes('reassign') || lowerMsg.includes('transfer')) && lowerMsg.includes('lead')) {
      return 'assign-lead';
    }
    // Check for edit lead
    if ((lowerMsg.includes('edit') || lowerMsg.includes('update') || lowerMsg.includes('modify')) && lowerMsg.includes('lead')) {
      return 'edit-lead';
    }
    // Check for create lead
    if ((lowerMsg.includes('create') || lowerMsg.includes('add') || lowerMsg.includes('new')) && lowerMsg.includes('lead')) {
      return 'lead';
    }
    // Check for create deal
    if ((lowerMsg.includes('create') || lowerMsg.includes('add') || lowerMsg.includes('new')) && lowerMsg.includes('deal')) {
      return 'deal';
    }
    // Check for schedule meeting
    if ((lowerMsg.includes('schedule') || lowerMsg.includes('create') || lowerMsg.includes('add')) && lowerMsg.includes('meeting')) {
      return 'meeting';
    }
    // Check for create task
    if ((lowerMsg.includes('create') || lowerMsg.includes('add') || lowerMsg.includes('new')) && lowerMsg.includes('task')) {
      return 'task';
    }
    
    return null;
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() && activeAction) {
      // Skip optional field
      setCurrentStep(currentStep + 1);
      const aiContent = getNextQuestion(activeAction, currentStep + 1);
      typewriterEffect(aiContent, () => {
        const aiResponse = {
          id: messages.length + 1,
          type: 'ai',
          content: aiContent,
          timestamp: new Date().toLocaleTimeString(),
        };
        setMessages((prev) => [...prev, aiResponse]);
      });
      return;
    }

    if (!inputMessage.trim()) return;

    const userMessage = {
      id: messages.length + 1,
      type: 'user',
      content: inputMessage,
      timestamp: new Date().toLocaleTimeString(),
    };

    if (activeAction) {
      const fieldMap = {
        lead: ['firstName', 'lastName', 'email', 'phone', 'status', 'priority', 'company'],
        'edit-lead': ['leadEmail', 'fieldToUpdate', 'newValue'],
        'assign-lead': ['leadEmail', 'assignToUserId'],
        deal: ['title', 'value', 'closeDate', 'contactFirstName', 'contactLastName', 'contactEmail', 'stage', 'probability', 'source'],
        meeting: ['title', 'dateTime', 'duration', 'type'],
        task: ['title', 'dueDate', 'priority', 'description'],
      };

      const fields = fieldMap[activeAction];
      if (fields && currentStep < fields.length) {
        // Special handling for edit-lead email step
        if (activeAction === 'edit-lead' && currentStep === 0) {
          console.log('🔍 Edit-lead: Searching for email:', inputMessage);

          // Validate email format first
          if (!validateEmail(inputMessage)) {
            setMessages((prev) => [...prev, userMessage]);
            const validationError = 'Invalid email format. Please enter a valid email address (e.g., user@example.com). Special characters like #, $, % are not allowed.';
            typewriterEffect(validationError, () => {
              const aiResponse = {
                id: messages.length + 2,
                type: 'ai',
                content: validationError,
                timestamp: new Date().toLocaleTimeString(),
              };
              setMessages((prev) => [...prev, aiResponse]);
            });
            return; // Don't proceed with search
          }

          // This is the email input, search for the lead
          setMessages((prev) => [...prev, userMessage]);
          await searchLeadByEmail(inputMessage);
          // searchLeadByEmail will handle the next step
        }
        // Special handling for assign-lead email step
        else if (activeAction === 'assign-lead' && currentStep === 0) {
          console.log('🔍 Assign-lead: Searching for email:', inputMessage);

          // Validate email format first
          if (!validateEmail(inputMessage)) {
            setMessages((prev) => [...prev, userMessage]);
            const validationError = 'Invalid email format. Please enter a valid email address (e.g., user@example.com). Special characters like #, $, % are not allowed.';
            typewriterEffect(validationError, () => {
              const aiResponse = {
                id: messages.length + 2,
                type: 'ai',
                content: validationError,
                timestamp: new Date().toLocaleTimeString(),
              };
              setMessages((prev) => [...prev, aiResponse]);
            });
            return; // Don't proceed with search
          }

          setMessages((prev) => [...prev, userMessage]);
          const found = await searchLeadByEmail(inputMessage);
          if (found) {
            // Fetch team members and show them
            const members = await fetchTeamMembers();
            if (members.length > 0) {
              let membersList = members.map((m, idx) => `${idx + 1}. ${m.firstName} ${m.lastName} (${m.email})`).join('\n');
              const aiContent = `Great! I found the lead. Here are the available team members:\n\n${membersList}\n\nPlease enter the number or email of the person you want to assign this lead to.`;
              typewriterEffect(aiContent, () => {
                const aiMsg = {
                  id: messages.length + 1,
                  type: 'ai',
                  content: aiContent,
                  timestamp: new Date().toLocaleTimeString(),
                };
                setMessages((prev) => [...prev, aiMsg]);
              });
            }
          }
        } 
        // Special handling for edit-lead newValue (step 2) - validate based on fieldToUpdate
        else if (activeAction === 'edit-lead' && currentStep === 2) {
          console.log('✏️ Edit-lead: Validating new value for field:', formData.fieldToUpdate);

          let validationError = null;
          const fieldToUpdate = formData.fieldToUpdate;

          // Validate based on which field is being updated
          if (fieldToUpdate === 'firstName' || fieldToUpdate === 'lastName') {
            if (!validateName(inputMessage)) {
              validationError = 'Invalid name format. Name must contain at least 2 letters and can only include letters, spaces, hyphens, and apostrophes. Numbers and special characters are not allowed.';
            }
          } else if (fieldToUpdate === 'email') {
            if (!validateEmail(inputMessage)) {
              validationError = 'Invalid email format. Please enter a valid email address (e.g., user@example.com). Special characters like #, $, % are not allowed.';
            }
          } else if (fieldToUpdate === 'phone') {
            if (!validatePhone(inputMessage)) {
              validationError = 'Invalid phone number format. Please enter a valid phone number with 10-15 digits (e.g., 91981200XXXX).';
            }
          } else if (fieldToUpdate === 'status') {
            if (!validateStatus(inputMessage)) {
              validationError = 'Invalid status. Please enter one of the following: new, contacted, qualified, proposal, negotiation, won, or lost.';
            }
          } else if (fieldToUpdate === 'priority') {
            if (!validatePriority(inputMessage)) {
              validationError = 'Invalid priority. Please enter one of the following: low, medium, high, or urgent.';
            }
          }

          // If validation failed, show error and don't proceed
          if (validationError) {
            setMessages((prev) => [...prev, userMessage]);

            typewriterEffect(validationError, () => {
              const aiResponse = {
                id: messages.length + 2,
                type: 'ai',
                content: validationError,
                timestamp: new Date().toLocaleTimeString(),
              };
              setMessages((prev) => [...prev, aiResponse]);
            });
            return; // Don't proceed
          }

          // Validation passed, update formData with newValue
          const updatedFormData = { ...formData, newValue: inputMessage };
          setFormData(updatedFormData);
          setCurrentStep(currentStep + 1);
          setMessages((prev) => [...prev, userMessage]);

          const aiContent = `Perfect! I'll update the ${fieldToUpdate} to "${inputMessage}". Review the changes and click Update when ready.`;
          typewriterEffect(aiContent, () => {
            const aiMsg = {
              id: messages.length + 2,
              type: 'ai',
              content: aiContent,
              timestamp: new Date().toLocaleTimeString(),
            };
            setMessages((prev) => [...prev, aiMsg]);
          });
        }
        // Special handling for assign-lead user selection
        else if (activeAction === 'assign-lead' && currentStep === 1) {
          console.log('👤 Assign-lead: Selecting user:', inputMessage);
          // Check if input is a number (index) or email
          let selectedMember = null;
          const inputNum = parseInt(inputMessage);
          if (!isNaN(inputNum) && inputNum > 0 && inputNum <= teamMembers.length) {
            selectedMember = teamMembers[inputNum - 1];
          } else {
            // Search by email
            selectedMember = teamMembers.find(m => m.email.toLowerCase() === inputMessage.toLowerCase());
          }
          
          if (selectedMember) {
            const updatedFormData = { ...formData, assignToUserId: selectedMember._id, assignToUserName: `${selectedMember.firstName} ${selectedMember.lastName}` };
            setFormData(updatedFormData);
            setCurrentStep(currentStep + 1);
            setMessages((prev) => [...prev, userMessage]);
            
            const aiContent = `Perfect! I'll assign this lead to ${selectedMember.firstName} ${selectedMember.lastName}. Review the details and click Assign when ready.`;
            typewriterEffect(aiContent, () => {
              const aiMsg = {
                id: messages.length + 2,
                type: 'ai',
                content: aiContent,
                timestamp: new Date().toLocaleTimeString(),
              };
              setMessages((prev) => [...prev, aiMsg]);
            });
          } else {
            setMessages((prev) => [...prev, userMessage]);
            const aiContent = `I couldn't find that team member. Please enter a valid number from the list or a valid email address.`;
            typewriterEffect(aiContent, () => {
              const aiMsg = {
                id: messages.length + 2,
                type: 'ai',
                content: aiContent,
                timestamp: new Date().toLocaleTimeString(),
              };
              setMessages((prev) => [...prev, aiMsg]);
            });
          }
        }
        else {
          console.log('📝 Setting form data:', fields[currentStep], '=', inputMessage);

          // Validate input based on field type
          const currentField = fields[currentStep];
          let validationError = null;

          // Name validation (for firstName, lastName, contactFirstName, contactLastName)
          if (currentField === 'firstName' || currentField === 'lastName' ||
              currentField === 'contactFirstName' || currentField === 'contactLastName') {
            if (!validateName(inputMessage)) {
              validationError = 'Invalid name format. Name must contain at least 2 letters and can only include letters, spaces, hyphens, and apostrophes. Numbers and special characters are not allowed.';
            }
          }

          // Email validation
          if (currentField === 'email' || currentField === 'contactEmail') {
            if (!validateEmail(inputMessage)) {
              validationError = 'Invalid email format. Please enter a valid email address (e.g., user@example.com). Special characters like #, $, % are not allowed.';
            }
          }

          // Phone validation (E.164 format)
          if (currentField === 'phone') {
            if (!validatePhone(inputMessage)) {
              validationError = 'Invalid phone number format. Please enter a valid phone number with 10-15 digits (e.g., 9198980180228 or +919898018022).';
            }
          }

          // Status validation
          if (currentField === 'status') {
            if (!validateStatus(inputMessage)) {
              validationError = 'Invalid status. Please enter one of the following: new, contacted, qualified, proposal, negotiation, won, or lost.';
            }
          }

          // Priority validation
          if (currentField === 'priority') {
            if (!validatePriority(inputMessage)) {
              validationError = 'Invalid priority. Please enter one of the following: low, medium, high, or urgent.';
            }
          }

          // If validation failed, show error and don't proceed
          if (validationError) {
            setMessages((prev) => [...prev, userMessage]);

            typewriterEffect(validationError, () => {
              const aiResponse = {
                id: messages.length + 2,
                type: 'ai',
                content: validationError,
                timestamp: new Date().toLocaleTimeString(),
              };
              setMessages((prev) => [...prev, aiResponse]);
            });
            return; // Don't proceed to next step
          }

          // Validation passed, proceed with setting form data
          const updatedFormData = { ...formData, [fields[currentStep]]: inputMessage };
          setFormData(updatedFormData);
          setCurrentStep(currentStep + 1);
          console.log('📊 Updated formData:', updatedFormData);
          console.log('➡️ Moving to step:', currentStep + 1);

          setMessages((prev) => [...prev, userMessage]);

          // Use typewriter effect for AI response
          const aiContent = getNextQuestion(activeAction, currentStep + 1);
          typewriterEffect(aiContent, () => {
            const aiResponse = {
              id: messages.length + 2,
              type: 'ai',
              content: aiContent,
              timestamp: new Date().toLocaleTimeString(),
            };
            setMessages((prev) => [...prev, aiResponse]);
          });
        }
      } else {
        setMessages([...messages, userMessage]);
      }
    } else {
      // Check if message contains action keywords
      const detectedAction = detectActionFromMessage(inputMessage);
      
      if (detectedAction) {
        // Auto-trigger the detected action
        setMessages((prev) => [...prev, userMessage]);
        handleActionSelect(detectedAction);
      } else {
        setMessages((prev) => [...prev, userMessage]);

        // Use typewriter effect for AI response
        const aiContent = 'Please select what you would like to create:';
        typewriterEffect(aiContent, () => {
          const aiResponse = {
            id: messages.length + 2,
            type: 'ai',
            content: aiContent,
            timestamp: new Date().toLocaleTimeString(),
            showActions: true,
          };
          setMessages((prev) => [...prev, aiResponse]);
        });
      }
    }

    setInputMessage('');
  };

  const handleCreate = async () => {
    console.log('🚀 handleCreate called');
    console.log('📋 Current action:', activeAction);
    console.log('📦 Form data:', formData);
    
    setIsCreating(true);
    const currentAction = activeAction;
    
    try {
      let response;
      
      // Call appropriate API based on action type
      switch (currentAction) {
        case 'lead':
          console.log('Creating lead with data:', formData);
          response = await aiAgentService.createLead(formData);
          break;
        case 'edit-lead':
          console.log('Updating lead:', formData.leadId);
          console.log('Update data:', { [formData.fieldToUpdate]: formData.newValue });
          response = await aiAgentService.updateLead(formData.leadId, {
            [formData.fieldToUpdate]: formData.newValue
          });
          break;
        case 'assign-lead':
          console.log('Assigning lead:', formData.leadId, 'to user:', formData.assignToUserId);
          response = await aiAgentService.updateLead(formData.leadId, {
            assignedTo: formData.assignToUserId
          });
          break;
        case 'deal':
          response = await aiAgentService.createDeal(formData);
          break;
        case 'meeting':
          response = await aiAgentService.createMeeting(formData);
          break;
        case 'task':
          response = await aiAgentService.createTask(formData);
          break;
        default:
          throw new Error('Unknown action type');
      }

      const actionName = currentAction === 'edit-lead' ? 'Lead' : currentAction.charAt(0).toUpperCase() + currentAction.slice(1);
      const actionVerb = currentAction === 'edit-lead' ? 'updated' : 'created';
      
      antMessage.success(response.message || `${actionName} ${actionVerb} successfully!`);
      
      // Reset state
      setActiveAction(null);
      setFormData({});
      setCurrentStep(0);
      setIsFormComplete(false);
      setSelectedLead(null);
      setAvailableLeads([]);

      // Use typewriter effect for success message
      const aiContent = currentAction === 'edit-lead' 
        ? `Great! The lead has been updated successfully. What else would you like to do?`
        : `Great! Your ${currentAction} has been created successfully with ID: ${response.data._id}. What else would you like to do?`;
      typewriterEffect(aiContent, () => {
        const aiMsg = {
          id: messages.length + 1,
          type: 'ai',
          content: aiContent,
          timestamp: new Date().toLocaleTimeString(),
        };
        setMessages((prev) => [...prev, aiMsg]);
      });
    } catch (error) {
      console.error('Create error:', error);
      antMessage.error(error.message || `Failed to create ${currentAction}`);
      
      // Show error message in chat
      const errorContent = `I'm sorry, there was an error creating the ${currentAction}. ${error.message || 'Please try again.'}`;
      typewriterEffect(errorContent, () => {
        const aiMsg = {
          id: messages.length + 1,
          type: 'ai',
          content: errorContent,
          timestamp: new Date().toLocaleTimeString(),
        };
        setMessages((prev) => [...prev, aiMsg]);
      });
    } finally {
      setIsCreating(false);
    }
  };

  const renderPreview = () => {
    if (!activeAction) {
      return (
        <Empty
          description="Start a conversation to create leads, deals, meetings, or tasks"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          style={{ marginTop: '100px' }}
        />
      );
    }

    // Special handling for edit-lead
    if (activeAction === 'edit-lead') {
      if (!selectedLead) {
        // Waiting for lead search
        return (
          <Card title="Edit Lead" bordered={false}>
            {loadingLeads ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
                <div style={{ marginTop: '8px', color: '#666' }}>Searching for lead...</div>
              </div>
            ) : (
              <Empty 
                description="Provide the lead's email address in the chat to get started"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )}
          </Card>
        );
      } else {
        // Show selected lead with changes
        const updatedLead = { ...selectedLead, ...formData };
        return (
          <Card title="Lead Preview with Changes" bordered={false}>
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="First Name">
                <span style={{ color: formData.fieldToUpdate === 'firstName' ? '#1890ff' : 'inherit', fontWeight: formData.fieldToUpdate === 'firstName' ? 'bold' : 'normal' }}>
                  {formData.fieldToUpdate === 'firstName' && formData.newValue ? formData.newValue : selectedLead.firstName}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="Last Name">
                <span style={{ color: formData.fieldToUpdate === 'lastName' ? '#1890ff' : 'inherit', fontWeight: formData.fieldToUpdate === 'lastName' ? 'bold' : 'normal' }}>
                  {formData.fieldToUpdate === 'lastName' && formData.newValue ? formData.newValue : selectedLead.lastName}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="Email">
                <span style={{ color: formData.fieldToUpdate === 'email' ? '#1890ff' : 'inherit', fontWeight: formData.fieldToUpdate === 'email' ? 'bold' : 'normal' }}>
                  {formData.fieldToUpdate === 'email' && formData.newValue ? formData.newValue : selectedLead.email}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="Phone">
                <span style={{ color: formData.fieldToUpdate === 'phone' ? '#1890ff' : 'inherit', fontWeight: formData.fieldToUpdate === 'phone' ? 'bold' : 'normal' }}>
                  {formData.fieldToUpdate === 'phone' && formData.newValue ? formData.newValue : selectedLead.phone || '-'}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <span style={{ color: formData.fieldToUpdate === 'status' ? '#1890ff' : 'inherit', fontWeight: formData.fieldToUpdate === 'status' ? 'bold' : 'normal' }}>
                  {formData.fieldToUpdate === 'status' && formData.newValue ? formData.newValue : selectedLead.status}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="Priority">
                <span style={{ color: formData.fieldToUpdate === 'priority' ? '#1890ff' : 'inherit', fontWeight: formData.fieldToUpdate === 'priority' ? 'bold' : 'normal' }}>
                  {formData.fieldToUpdate === 'priority' && formData.newValue ? formData.newValue : selectedLead.priority}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="Company">
                <span style={{ color: formData.fieldToUpdate === 'company' ? '#1890ff' : 'inherit', fontWeight: formData.fieldToUpdate === 'company' ? 'bold' : 'normal' }}>
                  {formData.fieldToUpdate === 'company' && formData.newValue ? formData.newValue : selectedLead.company || '-'}
                </span>
              </Descriptions.Item>
            </Descriptions>

            {formData.fieldToUpdate && formData.newValue && (
              <div style={{ marginTop: '16px', padding: '12px', background: '#e6f7ff', borderRadius: '4px' }}>
                <div style={{ fontSize: '12px', color: '#0050b3', fontWeight: 'bold' }}>
                  Change Preview:
                </div>
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                  {formData.fieldToUpdate}: <span style={{ textDecoration: 'line-through' }}>{selectedLead[formData.fieldToUpdate] || 'empty'}</span> → <span style={{ color: '#1890ff', fontWeight: 'bold' }}>{formData.newValue}</span>
                </div>
              </div>
            )}

            {isFormComplete && (
              <Button
                type="primary"
                size="large"
                block
                icon={isCreating ? <LoadingOutlined /> : <CheckCircleOutlined />}
                onClick={handleCreate}
                loading={isCreating}
                disabled={isCreating}
                style={{ marginTop: '24px' }}
              >
                {isCreating ? 'Updating...' : 'Update Lead'}
              </Button>
            )}

            {!isFormComplete && !isCreating && (
              <Tag color="orange" style={{ marginTop: '16px' }}>
                Fill in all required fields in the chat to enable update
              </Tag>
            )}
          </Card>
        );
      }
    }

    // Default preview for other actions
    const getTitle = () => {
      return activeAction.charAt(0).toUpperCase() + activeAction.slice(1) + ' Preview';
    };

    return (
      <Card title={getTitle()} bordered={false}>
        <Descriptions column={1} bordered size="small">
          {Object.entries(formData).map(([key, value]) => (
            <Descriptions.Item 
              key={key} 
              label={key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}
            >
              {value || '-'}
            </Descriptions.Item>
          ))}
        </Descriptions>

        {isFormComplete && (
          <Button
            type="primary"
            size="large"
            block
            icon={isCreating ? <LoadingOutlined /> : <CheckCircleOutlined />}
            onClick={handleCreate}
            loading={isCreating}
            disabled={isCreating}
            style={{ marginTop: '24px' }}
          >
            {isCreating ? 'Creating...' : `Create ${activeAction.charAt(0).toUpperCase() + activeAction.slice(1)}`}
          </Button>
        )}

        {!isFormComplete && !isCreating && (
          <Tag color="orange" style={{ marginTop: '16px' }}>
            Fill in all required fields in the chat to enable creation
          </Tag>
        )}

        {isCreating && (
          <div style={{ marginTop: '16px', textAlign: 'center' }}>
            <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
            <div style={{ marginTop: '8px', color: '#1890ff' }}>
              Creating your {activeAction}...
            </div>
          </div>
        )}
      </Card>
    );
  };

  return (
    <div style={{ height: 'calc(100vh - 64px)', background: '#f0f2f5', overflow: 'hidden' }}>
      <Row style={{ height: '100%' }} gutter={0}>
        {/* Left Column: AI Chat Interface (40%) */}
        <Col span={10} style={{ height: '100%', borderRight: '1px solid #e8e8e8' }}>
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'white' }}>
            {/* Chat Header */}
            <div style={{ padding: '20px', background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)', color: 'white', borderBottom: '1px solid rgba(255, 255, 255, 0.2)' }}>
              <Space>
                <Avatar size={48} style={{ background: 'rgba(255, 255, 255, 0.2)', border: '2px solid white' }} icon={<RobotOutlined style={{ fontSize: '24px' }} />} />
                <div>
                  <div style={{ fontWeight: 'bold', fontSize: '18px' }}>AI Assistant</div>
                  <div style={{ fontSize: '12px', opacity: 0.9 }}>Always here to help</div>
                </div>
              </Space>
            </div>

            {/* Chat Messages */}
            <div ref={chatContainerRef} style={{ flex: 1, overflowY: 'auto', padding: '20px', background: '#fafafa' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {messages.map((message) => (
                  <div key={message.id}>
                    <div style={{ display: 'flex', justifyContent: message.type === 'user' ? 'flex-end' : 'flex-start' }}>
                      <div style={{ maxWidth: '75%', background: message.type === 'user' ? '#1890ff' : 'white', color: message.type === 'user' ? 'white' : '#000', padding: '12px 16px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                        <div style={{ fontSize: '14px', lineHeight: '1.6' }}>{message.content}</div>
                        <div style={{ fontSize: '11px', opacity: 0.7, marginTop: '8px', textAlign: 'right' }}>{message.timestamp}</div>
                      </div>
                    </div>

                    {/* Show action cards in chat */}
                    {message.showActions && !activeAction && (
                      <div style={{ marginTop: '12px', display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                        {actionCards.map((card) => (
                          <Card
                            key={card.key}
                            hoverable
                            size="small"
                            style={{ background: card.color, border: '1px solid #e8e8e8', cursor: 'pointer' }}
                            onClick={() => handleActionSelect(card.key)}
                          >
                            <Space direction="vertical" size="small" style={{ width: '100%', textAlign: 'center' }}>
                              {card.icon}
                              <div style={{ fontSize: '14px', fontWeight: 'bold' }}>{card.title}</div>
                            </Space>
                          </Card>
                        ))}
                      </div>
                    )}
                  </div>
                ))}

                {/* Typing indicator with typewriter effect */}
                {isTyping && typingMessage && (
                  <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                    <div style={{ maxWidth: '75%', background: 'white', color: '#000', padding: '12px 16px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                      <div style={{ fontSize: '14px', lineHeight: '1.6' }}>
                        {typingMessage}
                        <span style={{ animation: 'blink 1s infinite' }}>|</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Scroll anchor */}
                <div ref={messagesEndRef} />
              </div>
            </div>

            {/* CSS for blinking cursor */}
            <style>{`
              @keyframes blink {
                0%, 50% { opacity: 1; }
                51%, 100% { opacity: 0; }
              }
            `}</style>

            {/* Hints Section */}
            <div style={{ padding: '12px 20px', background: '#f5f5f5', borderTop: '1px solid #e8e8e8' }}>
              <div style={{ fontSize: '11px', color: '#666', marginBottom: '8px', fontWeight: '500' }}>
                💡 Quick Actions - Try saying:
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                <Tag color="blue" style={{ cursor: 'pointer', fontSize: '11px' }} onClick={() => setInputMessage('Create a lead')}>
                  Create Lead
                </Tag>
                <Tag color="cyan" style={{ cursor: 'pointer', fontSize: '11px' }} onClick={() => setInputMessage('Edit a lead')}>
                  Edit Lead
                </Tag>
                <Tag color="magenta" style={{ cursor: 'pointer', fontSize: '11px' }} onClick={() => setInputMessage('Assign a lead')}>
                  Assign Lead
                </Tag>
                <Tag color="green" style={{ cursor: 'pointer', fontSize: '11px' }} onClick={() => setInputMessage('Create a deal')}>
                  Create Deal
                </Tag>
                <Tag color="orange" style={{ cursor: 'pointer', fontSize: '11px' }} onClick={() => setInputMessage('Schedule a meeting')}>
                  Schedule Meeting
                </Tag>
                <Tag color="purple" style={{ cursor: 'pointer', fontSize: '11px' }} onClick={() => setInputMessage('Create a task')}>
                  Create Task
                </Tag>
              </div>
            </div>

            {/* Chat Input */}
            <div style={{ padding: '16px 20px', background: 'white', borderTop: '1px solid #e8e8e8' }}>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
                <TextArea
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  placeholder="Type your message..."
                  autoSize={{ minRows: 1, maxRows: 4 }}
                  style={{ flex: 1, borderRadius: '20px', padding: '10px 16px' }}
                  onPressEnter={(e) => {
                    if (!e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                />
                <Button type="primary" shape="circle" icon={<SendOutlined />} size="large" onClick={handleSendMessage} style={{ width: '48px', height: '48px' }} />
              </div>
              <div style={{ fontSize: '11px', color: '#999', marginTop: '8px', textAlign: 'center' }}>
                Press Enter to send • Shift+Enter for new line
              </div>
            </div>
          </div>
        </Col>

        {/* Right Column: Preview & Submit (60%) */}
        <Col span={14} style={{ height: '100%', overflow: 'auto', background: 'white' }}>
          <div style={{ padding: '24px' }}>
            {renderPreview()}
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default AIAgentPage;
